import { z } from "zod";
import { tradeCodeSchema, type TradeCode } from "./catalog.js";
import type { CanonicalProjectType } from "./intake-v2.js";

export { tradeCodeSchema };
export type { TradeCode } from "./catalog.js";

export const jobStatusSchema = z.enum([
  "lead",
  "quote_awaiting_approval",
  "quote_sent",
  "quote_accepted",
  "in_progress",
  "completed",
  "closed",
  "declined",
  "cancelled",
]);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const paymentStatusSchema = z.enum(["not_started", "waiting_payment", "paid"]);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

export const materialMarketTierSchema = z.enum([
  "economy",
  "standard",
  "mid_market",
  "premium",
  "luxury",
]);
export type MaterialMarketTier = z.infer<typeof materialMarketTierSchema>;

export const flooringMaterialFamilySchema = z.enum([
  "sheet_vinyl",
  "vinyl_plank",
  "laminate",
  "engineered_hardwood",
  "solid_hardwood",
  "tile",
  "other",
  "unsure",
]);
export type FlooringMaterialFamily = z.infer<typeof flooringMaterialFamilySchema>;

export const projectStatusSchema = z.enum([
  "exploring",
  "draft",
  "ready_for_estimate",
  "estimated",
  "submitted",
  "contractor_reviewing",
  "walkthrough_requested",
  "walkthrough_scheduled",
  "walkthrough_completed",
  "final_quote_issued",
  "accepted",
  "declined",
  "archived",
]);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

export const moneyRangeSchema = z
  .object({
    currency: z.string().length(3).default("CAD"),
    lowMinor: z.string().regex(/^\d+$/),
    highMinor: z.string().regex(/^\d+$/),
  })
  .refine((value) => BigInt(value.lowMinor) <= BigInt(value.highMinor), {
    message: "lowMinor must not exceed highMinor",
  });
export type MoneyRange = z.infer<typeof moneyRangeSchema>;

export const rectangleSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1).max(120),
  widthMm: z.number().int().positive().max(100_000),
  lengthMm: z.number().int().positive().max(100_000),
  offsetXMm: z.number().int().min(0).default(0),
  offsetYMm: z.number().int().min(0).default(0),
});
export type Rectangle = z.infer<typeof rectangleSchema>;

export const projectSpaceSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  kind: z.enum(["room", "closet"]),
  shape: z.enum(["rectangle", "l_shape"]),
  rectangles: z.array(rectangleSchema).min(1).max(2),
  exclusions: z.array(rectangleSchema).default([]),
  removeExistingFlooring: z.boolean().default(false),
  requiresStairReview: z.boolean().default(false),
});
export type ProjectSpace = z.infer<typeof projectSpaceSchema>;

export const transitionSchema = z.object({
  id: z.string(),
  label: z.string().min(1).max(120),
  lengthMm: z.number().int().positive(),
});
export type Transition = z.infer<typeof transitionSchema>;

export const materialIntentSchema = z.object({
  family: flooringMaterialFamilySchema,
  tier: materialMarketTierSchema,
});
export type MaterialIntent = z.infer<typeof materialIntentSchema>;

export const projectSnapshotV1Schema = z.object({
  schemaVersion: z.literal(1),
  projectId: z.uuid(),
  contractorSlug: z.string().min(2),
  projectType: z.literal("flooring"),
  spaces: z.array(projectSpaceSchema).min(1),
  transitions: z.array(transitionSchema).default([]),
  materialIntent: materialIntentSchema,
  answers: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  confirmedAt: z.iso.datetime(),
});
export type ProjectSnapshotV1 = z.infer<typeof projectSnapshotV1Schema>;

export const areaValueSchema = z.object({
  squareMillimetres: z.string().regex(/^\d+$/),
  squareFeet: z.string().regex(/^\d+(\.\d+)?$/),
});
export type AreaValue = z.infer<typeof areaValueSchema>;

export const quantityResultSchema = z.object({
  engineVersion: z.string(),
  measuredArea: areaValueSchema,
  excludedArea: areaValueSchema,
  netArea: areaValueSchema,
  measurementAllowance: areaValueSchema,
  planningArea: areaValueSchema,
  materialWaste: areaValueSchema,
  requiredMaterial: areaValueSchema,
  transitionLengthMm: z.string().regex(/^\d+$/),
  removalArea: areaValueSchema,
  stairReviewRequired: z.boolean(),
  warnings: z.array(z.string()),
});
export type QuantityResult = z.infer<typeof quantityResultSchema>;

export const estimateLineItemSchema = z.object({
  tradeCode: tradeCodeSchema.optional(),
  code: z.string(),
  category: z.enum([
    "material",
    "labour",
    "removal",
    "disposal",
    "travel",
    "consumables",
    "fee",
    "markup",
    "contingency",
    "minimum_charge",
    "tax",
  ]),
  description: z.string(),
  quantity: z.string(),
  baseQuantity: z.string().optional(),
  measurementAllowanceQuantity: z.string().optional(),
  wasteQuantity: z.string().optional(),
  unit: z.enum(["square_foot", "linear_foot", "hour", "item", "fixed"]),
  amount: moneyRangeSchema,
  taxable: z.boolean(),
  markupApplies: z.boolean().optional(),
  contingencyApplies: z.boolean().optional(),
});
export type EstimateLineItem = z.infer<typeof estimateLineItemSchema>;

export const estimateResultSchema = z.object({
  engineVersion: z.string(),
  currency: z.string().length(3),
  lineItems: z.array(estimateLineItemSchema),
  subtotal: moneyRangeSchema,
  tax: moneyRangeSchema,
  total: moneyRangeSchema,
  disclaimer: z.literal("Planning estimate — not a binding contractor quote."),
});
export type EstimateResult = z.infer<typeof estimateResultSchema>;

export type Principal =
  | { kind: "anonymous"; sessionId: string }
  | { kind: "customer"; userId: string; sessionId: string }
  | { kind: "contractor"; userId: string; membershipId: string; role: "staff" | "owner" }
  | { kind: "platform_admin"; userId: string }
  | { kind: "botyguard"; serviceAccount: string; agentId: string; sessionId: string };

export interface CreateQuoteAgentSession {
  projectId: string;
  projectType: string;
  trades?: TradeCode[];
  requestedTrades?: string[];
  coveredTrades?: string[];
  uncoveredTrades?: string[];
  locale: string;
}

export interface SendQuoteAgentMessage {
  sessionId: string;
  projectId: string;
  message: string;
  clientMessageId: string;
}

export interface CloseQuoteAgentSession {
  sessionId: string;
  projectId: string;
}

export interface AgentSession {
  sessionId: string;
  agentId: string;
  projectId: string;
  status?: "active" | "closed";
  responses: AgentUiResponse[];
}

export type AgentUiResponse =
  | { type: "message"; id: string; text: string }
  | { type: "requirement"; id: string; requirement: string }
  | { type: "progress"; id: string; completed: number; total: number };

export interface AgentTranscriptMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: string;
}

export interface AgentMessageStatus {
  sessionId: string;
  messageId: string;
  clientMessageId: string;
  runId: string;
  state: "queued" | "running" | "completed" | "failed" | "cancelled";
  acceptedAt: string;
  finishedAt?: string | null;
  error?: string | null;
  suggestedTitle?: string | null;
  assistant?: { id: string; text: string } | null;
}

export interface AnalyzeProjectCapture {
  analysisJobId: string;
  projectId: string;
  captureId: string;
  capability: string;
  projectTypes: CanonicalProjectType[];
  allowedObservationFields: string[];
}

export interface ProjectCaptureObservation {
  field: string;
  proposedValue: unknown;
  semanticConfidence: number;
  evidenceRegion?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  explanation?: string | null;
  requiresCustomerConfirmation: boolean;
}

export interface ProjectCaptureAnalysis {
  analysisJobId: string;
  provider: string;
  model: string;
  promptVersion: string;
  quality: {
    status: "acceptable" | "warning" | "rejected";
    issues: string[];
  };
  observations: ProjectCaptureObservation[];
  warnings: string[];
}

export * from "./intake-v2.js";
export * from "./runbook.js";

export interface QuoteAgentGateway {
  createSession(input: CreateQuoteAgentSession): Promise<AgentSession>;
  sendMessage(input: SendQuoteAgentMessage): Promise<AgentMessageStatus>;
  getMessage(input: {
    sessionId: string;
    projectId: string;
    messageId: string;
  }): Promise<AgentMessageStatus>;
  closeSession(input: CloseQuoteAgentSession): Promise<void>;
  reopenSession(input: CloseQuoteAgentSession): Promise<void>;
  analyzeCapture(input: AnalyzeProjectCapture): Promise<ProjectCaptureAnalysis>;
}
